/**
 * A11Y Bar:
 * - Dark mode switcher.
 * - Text size modifier.
 * - Line spacing modifier.
 * - Dyslexia font switcher.
 *
 * @author MNCA
 */

class A11yBar {

    constructor() {
        this.settings = Object.assign(new A11Bar_Settings(), this.retrieveSettings());
        this.checkboxThemeSwitch = document.getElementById('themeSwitch');
        this.btnTxtMore = document.getElementById('btnTxtMore');
        this.btnTxtLess = document.getElementById('btnTxtLess');
        this.btnLineMore = document.getElementById('btnLineMore');
        this.btnLineLess = document.getElementById('btnLineLess');
        this.btnDyslexia = document.getElementById('btnFontDyslexie');
        this.btnA11yReset = document.getElementById('btnA11yReset');
        this.initEventListeners();
        this.reApplySettings();
    }

    static isDarkMode() {
        return globalThis.matchMedia?.("(prefers-color-scheme:dark)").matches ?? false;
    }

    saveSettings(settings = {}) {
        if (Object.keys(settings).length === 0) {
            localStorage.setItem("a11y.settings", JSON.stringify(this.settings));

            return this.settings;
        }

        localStorage.setItem("a11y.settings", JSON.stringify(settings));

        return settings;
    }

    storeDefaultSettings() {
        localStorage.setItem("a11y.settings", JSON.stringify(new A11Bar_Settings()));
    }

    retrieveSettings() {
        return JSON.parse(localStorage.getItem("a11y.settings")) || this.storeDefaultSettings();
    }

    getSettings() {
        return this.settings;
    }

    setSettings(settings = {}) {
        this.saveSettings(settings);
        this.settings = settings;
    }

    reApplySettings() {
        this.applySetting('theme', this.settings.theme, false);
        this.applySetting('textSize', this.settings.textSize, false);
        this.setButtonsTxtSizeState(this.settings.textSize);
        this.applySetting('lineSpacing', this.settings.lineSpacing, false);
        this.setButtonsLineSpacingState(this.settings.lineSpacing);
        this.applySetting('dyslexiaFont', this.settings.dyslexiaFont, false);
    }

    applySetting(name = '', value, save = true) {
        switch (name) {
            case 'theme':
                document.body.setAttribute('data-theme', value);
                /** @type HTMLInputElement **/
                const themeSwitch = document.getElementById('themeSwitch');
                themeSwitch.checked = ('dark' === value) || false;
                break;
            case 'textSize':
                document.body.setAttribute("data-size", value.toString());
                break;
            case 'lineSpacing':
                document.body.setAttribute("data-line", value.toString());
                break;
            case 'dyslexiaFont':
                if (value === true) {
                    document.body.classList.add("font-variant");
                    this.btnDyslexia.innerText = "Désactiver";
                } else {
                    document.body.classList.remove("font-variant");
                    this.btnDyslexia.innerText = "Activer";
                }
                break;
        }

        if (save) {
            this.settings[name] = value;
            this.saveSettings();
        }
    }

    setButtonsTxtSizeState(textSize = 0) {
        if (textSize > 0) {
            this.btnTxtLess.removeAttribute("disabled")
        }
        if (textSize === 2) {
            this.btnTxtMore.setAttribute("disabled", "")
        }
        if (textSize < 2) {
            this.btnTxtMore.removeAttribute("disabled")
        }
        if (textSize === 0) {
            this.btnTxtLess.setAttribute("disabled", "")
        }
    }

    setButtonsLineSpacingState(lineSpacing = 0) {
        if (lineSpacing > 0) {
            this.btnLineLess.removeAttribute("disabled")
        }
        if (lineSpacing === 2) {
            this.btnLineMore.setAttribute("disabled", "")
        }
        if (lineSpacing < 2) {
            this.btnLineMore.removeAttribute("disabled")
        }
        if (lineSpacing === 0) {
            this.btnLineLess.setAttribute("disabled", "")
        }
    }

    initEventListeners() {
        let that = this;

        // Dark mode theme switch.
        this.checkboxThemeSwitch.addEventListener('change', function (event) {
            let theme = (that.checkboxThemeSwitch.checked) ? 'dark' : 'light';

            that.applySetting('theme', theme);
        });

        // Text size modifier.
        this.btnTxtMore.addEventListener('click', function () {
            let textSize = that.settings.textSize;

            if (textSize < 2) {
                textSize++;
            }

            that.setButtonsTxtSizeState(textSize);
            that.applySetting('textSize', textSize);
        });

        this.btnTxtLess.addEventListener('click', function () {
            let textSize = that.settings.textSize;

            if (textSize > 0) {
                textSize--;
            }

            that.setButtonsTxtSizeState(textSize);
            that.applySetting('textSize', textSize);
        });

        // Line spacing modifier.
        this.btnLineMore.addEventListener('click', function () {
            let lineSpacing = that.settings.lineSpacing;

            if (lineSpacing < 2) {
                lineSpacing++;
            }
            that.setButtonsLineSpacingState(lineSpacing);
            that.applySetting('lineSpacing', lineSpacing);
        });

        this.btnLineLess.addEventListener('click', function () {
            let lineSpacing = that.settings.lineSpacing;

            if (lineSpacing > 0) {
                lineSpacing--;
            }
            that.setButtonsLineSpacingState(lineSpacing);
            that.applySetting('lineSpacing', lineSpacing);
        });

        // Dyslexia font switch.
        this.btnDyslexia.addEventListener('click', function () {
            that.applySetting('dyslexiaFont', !that.settings.dyslexiaFont);
        });

        // Default values.
        this.btnA11yReset.addEventListener('click', function () {
            that.settings = Object.assign(that.settings, new A11Bar_Settings());
            that.saveSettings();
            that.reApplySettings();
        });

    }

    logSettings() {
        console.log(this.settings);
    }
}

/**
 * A11Y Settings
 *
 * @author MNCA
 */
class A11Bar_Settings {
    theme = A11yBar.isDarkMode() ? 'dark' : 'light';
    textSize = 0;
    lineSpacing = 0;
    dyslexiaFont = false;
}

if (typeof window !== 'undefined') {
    window.A11yBar = new A11yBar();
}

export default A11yBar;